"use client"

import { useState } from "react"
import { Trash2, Calculator, Copy, Printer, Share2 } from "lucide-react"

type Tab = "detailed" | "simple" | "percentage"

type EmployeeRow = {
   id: number
   name: string
   hours: string
   rate: string
   otHours: string
   otRate: string
}

type Result = {
   pct: number
   total: number
   revenue: number
   wages: number
   overtime: number
   benefits: number
   taxes: number
   other: number
   statusLabel: string
   statusColor: string
   statusNote: string
}

const INITIAL_ROWS: EmployeeRow[] = [
   { id: 1, name: "Server 1", hours: "40", rate: "15", otHours: "0", otRate: "" },
   { id: 2, name: "Server 2", hours: "35", rate: "15", otHours: "0", otRate: "" },
   { id: 3, name: "Cook 1", hours: "45", rate: "18", otHours: "5", otRate: "" },
]

function rowCost(row: EmployeeRow): number {
   const h = parseFloat(row.hours) || 0
   const r = parseFloat(row.rate) || 0
   const ot = parseFloat(row.otHours) || 0
   const otr = parseFloat(row.otRate) || r * 1.5
   return h * r + ot * otr
}

function getStatus(pct: number): { label: string; color: string; note: string } {
   if (pct < 20)
      return { label: "Very Low", color: "#c77c00", note: "Very low — check if all labor costs are included." }
   if (pct < 30) return { label: "Excellent", color: "#1a7a3c", note: "Efficient cost control — well done." }
   if (pct <= 35) return { label: "Good", color: "#1e5fa8", note: "Industry sweet spot for most restaurant formats." }
   if (pct <= 40) return { label: "Borderline", color: "#c77c00", note: "Review scheduling and overtime spend." }
   return { label: "High", color: "#C8102E", note: "Affecting profitability — take action." }
}

function fmt(n: number) {
   return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const baseInput =
   "w-full rounded border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-[#EB5A23] focus:ring-2 focus:ring-[#EB5A23]/10"
const prefixInput =
   "w-full rounded border border-gray-200 py-2 pl-7 pr-3 text-sm text-gray-900 outline-none transition focus:border-[#EB5A23] focus:ring-2 focus:ring-[#EB5A23]/10"

const TABS: { id: Tab; label: string }[] = [
   { id: "detailed", label: "👥 Employee Roster" },
   { id: "simple", label: "📊 Quick Summary" },
   { id: "percentage", label: "📋 Labor % Check" },
]

export default function LaborCalculatorCard() {
   const [tab, setTab] = useState<Tab>("detailed")
   const [nextId, setNextId] = useState(4)
   const [rows, setRows] = useState<EmployeeRow[]>(() => INITIAL_ROWS.map((r) => ({ ...r })))

   // Detailed tab extras
   const [dBenefits, setDBenefits] = useState("")
   const [dTaxes, setDTaxes] = useState("")
   const [dRevenue, setDRevenue] = useState("")

   // Simple tab
   const [sWages, setSWages] = useState("")
   const [sOvertime, setSOvertime] = useState("")
   const [sBenefits, setSBenefits] = useState("")
   const [sTaxes, setSTaxes] = useState("")
   const [sOther, setSOther] = useState("")
   const [sRevenue, setSRevenue] = useState("")

   // Percentage tab
   const [pLabor, setPLabor] = useState("")
   const [pRevenue, setPRevenue] = useState("")

   const [result, setResult] = useState<Result | null>(null)
   const [error, setError] = useState<string | null>(null)
   const [resultCopied, setResultCopied] = useState(false)
   const [shareCopied, setShareCopied] = useState(false)

   function addRow() {
      setRows((prev) => [...prev, { id: nextId, name: "", hours: "", rate: "", otHours: "", otRate: "" }])
      setNextId((n) => n + 1)
   }

   function removeRow(id: number) {
      setRows((prev) => prev.filter((r) => r.id !== id))
   }

   function updateRow(id: number, field: keyof Omit<EmployeeRow, "id">, value: string) {
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)))
   }

   function switchTab(t: Tab) {
      setTab(t)
      setResult(null)
      setError(null)
   }

   function calculate() {
      setError(null)
      let wages = 0,
         overtime = 0,
         benefits = 0,
         taxes = 0,
         other = 0,
         revenue = 0,
         total = 0

      if (tab === "detailed") {
         revenue = parseFloat(dRevenue) || 0
         if (revenue <= 0) {
            setError("Please enter your total weekly revenue.")
            return
         }
         wages = rows.reduce((sum, r) => sum + (parseFloat(r.hours) || 0) * (parseFloat(r.rate) || 0), 0)
         overtime = rows.reduce((sum, r) => {
            const rate = parseFloat(r.rate) || 0
            return sum + (parseFloat(r.otHours) || 0) * (parseFloat(r.otRate) || rate * 1.5)
         }, 0)
         if (wages === 0 && overtime === 0) {
            setError("Please add at least one employee with hours and rate.")
            return
         }
         benefits = parseFloat(dBenefits) || 0
         taxes = parseFloat(dTaxes) || 0
         total = wages + overtime + benefits + taxes
      } else if (tab === "simple") {
         wages = parseFloat(sWages) || 0
         revenue = parseFloat(sRevenue) || 0
         if (revenue <= 0) {
            setError("Please enter your total revenue.")
            return
         }
         if (wages === 0) {
            setError("Please enter at least total wages.")
            return
         }
         overtime = parseFloat(sOvertime) || 0
         benefits = parseFloat(sBenefits) || 0
         taxes = parseFloat(sTaxes) || 0
         other = parseFloat(sOther) || 0
         total = wages + overtime + benefits + taxes + other
      } else {
         total = parseFloat(pLabor) || 0
         revenue = parseFloat(pRevenue) || 0
         if (total <= 0 || revenue <= 0) {
            setError("Please enter both labor cost and revenue.")
            return
         }
      }

      const pct = (total / revenue) * 100
      const s = getStatus(pct)
      setResult({
         pct,
         total,
         revenue,
         wages,
         overtime,
         benefits,
         taxes,
         other,
         statusLabel: s.label,
         statusColor: s.color,
         statusNote: s.note,
      })
   }

   function reset() {
      setRows(INITIAL_ROWS.map((r) => ({ ...r })))
      setNextId(4)
      setDBenefits("")
      setDTaxes("")
      setDRevenue("")
      setSWages("")
      setSOvertime("")
      setSBenefits("")
      setSTaxes("")
      setSOther("")
      setSRevenue("")
      setPLabor("")
      setPRevenue("")
      setResult(null)
      setError(null)
   }

   function copyResult() {
      if (!result) return
      navigator.clipboard
         .writeText(
            `Labor Cost Calculator — restaurantassociation.com\nLabor Cost %: ${result.pct.toFixed(2)}%\nTotal Labor Cost: $${result.total.toFixed(2)}\nTotal Revenue: $${result.revenue.toFixed(2)}\nStatus: ${result.statusLabel}`,
         )
         .catch(() => null)
      setResultCopied(true)
      setTimeout(() => setResultCopied(false), 2000)
   }

   async function shareLink() {
      const url = window.location.href
      if (navigator.share) {
         try {
            await navigator.share({ title: "Labor Cost Calculator", text: "Calculate your restaurant labor cost percentage.", url })
            return
         } catch {
            // cancelled or unsupported — fall through
         }
      }
      await navigator.clipboard.writeText(url).catch(() => null)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
   }

   return (
      <>
         <div
            className="mb-6 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
            style={{ borderTop: "3px solid #EB5A23" }}
         >
            {/* Header */}
            <div className="px-7 pt-7">
               <h2 className="mb-1 text-custom-3xl-bold">Labor Cost Calculator</h2>
               <p className="mb-5 text-custom-lg-normal text-gray-400">
                  Choose a method: build a full employee roster, or enter a quick summary of your costs.
               </p>

               {/* Tabs */}
               <div className="mb-6 flex overflow-hidden rounded-md border border-gray-200">
                  {TABS.map(({ id, label }) => (
                     <button
                        key={id}
                        onClick={() => switchTab(id)}
                        className={`flex-1 border-r border-gray-200 py-2.5 text-custom-sm-bold transition-colors last:border-r-0 ${tab === id ? "bg-[#EB5A23] text-white" : "bg-gray-50 text-gray-500 hover:bg-orange-50 hover:text-[#EB5A23]"}`}
                     >
                        {label}
                     </button>
                  ))}
               </div>
            </div>

            <div className="px-7 pb-7">
               {/* TAB 1: Employee Roster */}
               {tab === "detailed" && (
                  <div>
                     <p className="mb-3 text-custom-sm-normal text-gray-400">
                        Add each employee and their details. Weekly cost is auto-calculated.
                     </p>
                     <div className="mb-3 overflow-x-auto">
                        <table className="w-full min-w-[640px] border-collapse text-sm">
                           <thead>
                              <tr className="bg-gray-900 text-white">
                                 {[
                                    "Name / Role",
                                    "Hours/Week",
                                    "Hourly Rate ($)",
                                    "OT Hours",
                                    "OT Rate ($)",
                                    "Weekly Cost",
                                    "",
                                 ].map((h) => (
                                    <th
                                       key={h}
                                       className="px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wide"
                                    >
                                       {h}
                                    </th>
                                 ))}
                              </tr>
                           </thead>
                           <tbody>
                              {rows.map((row) => (
                                 <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
                                    <td className="px-2 py-1.5">
                                       <input
                                          type="text"
                                          value={row.name}
                                          onChange={(e) => updateRow(row.id, "name", e.target.value)}
                                          placeholder="e.g. Sarah"
                                          className={baseInput}
                                       />
                                    </td>
                                    <td className="px-2 py-1.5">
                                       <input
                                          type="number"
                                          min={0}
                                          step={0.5}
                                          value={row.hours}
                                          onChange={(e) => updateRow(row.id, "hours", e.target.value)}
                                          placeholder="40"
                                          className={baseInput}
                                       />
                                    </td>
                                    <td className="px-2 py-1.5">
                                       <input
                                          type="number"
                                          min={0}
                                          step={0.01}
                                          value={row.rate}
                                          onChange={(e) => updateRow(row.id, "rate", e.target.value)}
                                          placeholder="15.00"
                                          className={baseInput}
                                       />
                                    </td>
                                    <td className="px-2 py-1.5">
                                       <input
                                          type="number"
                                          min={0}
                                          step={0.5}
                                          value={row.otHours}
                                          onChange={(e) => updateRow(row.id, "otHours", e.target.value)}
                                          placeholder="0"
                                          className={baseInput}
                                       />
                                    </td>
                                    <td className="px-2 py-1.5">
                                       <input
                                          type="number"
                                          min={0}
                                          step={0.01}
                                          value={row.otRate}
                                          onChange={(e) => updateRow(row.id, "otRate", e.target.value)}
                                          placeholder="auto"
                                          className={baseInput}
                                       />
                                    </td>
                                    <td className="px-2 py-1.5">
                                       <input
                                          readOnly
                                          value={`$${fmt(rowCost(row))}`}
                                          className={`${baseInput} cursor-default bg-gray-50 font-semibold text-gray-700`}
                                       />
                                    </td>
                                    <td className="px-2 py-1.5">
                                       <button
                                          onClick={() => removeRow(row.id)}
                                          className="rounded p-1 text-gray-300 transition hover:bg-red-50 hover:text-red-500"
                                       >
                                          <Trash2 className="h-4 w-4" />
                                       </button>
                                    </td>
                                 </tr>
                              ))}
                           </tbody>
                        </table>
                     </div>

                     <button
                        onClick={addRow}
                        className="mb-5 flex items-center gap-1.5 rounded border border-dashed border-gray-200 px-3 py-2 text-sm font-semibold text-gray-400 transition hover:border-[#EB5A23] hover:bg-orange-50 hover:text-[#EB5A23]"
                     >
                        + Add Employee
                     </button>

                     <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                           <label className="mb-1.5 block text-custom-sm-bold uppercase tracking-wider text-gray-700">
                              Benefits / Week
                           </label>
                           <div className="relative">
                              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">
                                 $
                              </span>
                              <input
                                 type="number"
                                 min={0}
                                 step="any"
                                 value={dBenefits}
                                 onChange={(e) => setDBenefits(e.target.value)}
                                 placeholder="0.00"
                                 className={prefixInput}
                              />
                           </div>
                        </div>
                        <div>
                           <label className="mb-1.5 block text-custom-sm-bold uppercase  text-gray-700">
                              Payroll Taxes / Week
                           </label>
                           <div className="relative">
                              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">
                                 $
                              </span>
                              <input
                                 type="number"
                                 min={0}
                                 step="any"
                                 value={dTaxes}
                                 onChange={(e) => setDTaxes(e.target.value)}
                                 placeholder="0.00"
                                 className={prefixInput}
                              />
                           </div>
                        </div>
                     </div>

                     <div>
                        <label className="mb-1.5 block text-custom-sm-bold uppercase  text-gray-700">
                           Total Weekly Revenue <span className="text-[#EB5A23]">*</span>
                        </label>
                        <div className="relative">
                           <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">
                              $
                           </span>
                           <input
                              type="number"
                              min={0}
                              step="any"
                              value={dRevenue}
                              onChange={(e) => setDRevenue(e.target.value)}
                              placeholder="e.g. 25000"
                              className={prefixInput}
                           />
                        </div>
                     </div>
                  </div>
               )}

               {/* TAB 2: Quick Summary */}
               {tab === "simple" && (
                  <div>
                     <p className="mb-5 text-xs text-gray-400">
                        Enter your total costs for the period (weekly, monthly — just stay consistent).
                     </p>
                     <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {(
                           [
                              ["Total Wages & Salaries *", sWages, setSWages],
                              ["Total Overtime Pay", sOvertime, setSOvertime],
                              ["Employee Benefits", sBenefits, setSBenefits],
                              ["Payroll Taxes", sTaxes, setSTaxes],
                              ["Other Labor Costs", sOther, setSOther],
                              ["Total Revenue *", sRevenue, setSRevenue],
                           ] as [string, string, (v: string) => void][]
                        ).map(([label, val, set]) => (
                           <div key={label}>
                              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-700">
                                 {label}
                              </label>
                              <div className="relative">
                                 <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">
                                    $
                                 </span>
                                 <input
                                    type="number"
                                    min={0}
                                    step="any"
                                    value={val}
                                    onChange={(e) => set(e.target.value)}
                                    placeholder="0.00"
                                    className={prefixInput}
                                 />
                              </div>
                           </div>
                        ))}
                     </div>
                  </div>
               )}

               {/* TAB 3: Labor % Check */}
               {tab === "percentage" && (
                  <div>
                     <p className="mb-5 text-xs text-gray-400">
                        Already know your total labor spend? Just enter that and your revenue to check your percentage.
                     </p>
                     <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                           <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-700">
                              Total Labor Cost <span className="text-[#EB5A23]">*</span>
                           </label>
                           <div className="relative">
                              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">
                                 $
                              </span>
                              <input
                                 type="number"
                                 min={0}
                                 step="any"
                                 value={pLabor}
                                 onChange={(e) => setPLabor(e.target.value)}
                                 placeholder="e.g. 30000"
                                 className={prefixInput}
                              />
                           </div>
                        </div>
                        <div>
                           <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-700">
                              Total Revenue <span className="text-[#EB5A23]">*</span>
                           </label>
                           <div className="relative">
                              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">
                                 $
                              </span>
                              <input
                                 type="number"
                                 min={0}
                                 step="any"
                                 value={pRevenue}
                                 onChange={(e) => setPRevenue(e.target.value)}
                                 placeholder="e.g. 100000"
                                 className={prefixInput}
                              />
                           </div>
                        </div>
                     </div>
                  </div>
               )}

               {/* Calculate & Reset buttons */}
               <button
                  onClick={calculate}
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#EB5A23] py-4 text-custom-sm-bold uppercase  text-white transition hover:bg-[#cf4e1e] active:scale-[0.99]"
               >
                  <Calculator className="h-5 w-5" />
                  Calculate Labor Cost
               </button>
               <button
                  onClick={reset}
                  className="mt-3 w-full rounded-xl border border-gray-200 py-2.5 text-custom-sm-bold text-gray-600 transition hover:border-[#EB5A23] hover:text-[#EB5A23]"
               >
                  ↺ Reset Calculator
               </button>

               {/* Error state */}
               {error && (
                  <div className="mt-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-center text-sm font-semibold text-red-600">
                     ⚠ {error}
                  </div>
               )}

               {/* Result */}
               {result ? (
                  <div className="mt-4 rounded-xl border-2 bg-white p-6" style={{ borderColor: result.statusColor }}>
                     <p className="mb-1 text-xs font-bold uppercase tracking-wider text-gray-400">
                        Your Labor Cost Percentage
                     </p>
                     <p className="mb-5 font-serif text-5xl font-bold" style={{ color: result.statusColor }}>
                        {result.pct.toFixed(2)}%
                     </p>

                     <div className="mb-4 overflow-hidden rounded-lg border border-gray-100">
                        <div className="grid grid-cols-3 divide-x divide-gray-100">
                           <div className="px-4 py-4 text-center">
                              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-400">
                                 Total Labor Cost
                              </p>
                              <p className="font-serif text-xl font-bold text-gray-900">${fmt(result.total)}</p>
                              <p className="mt-1 text-xs text-gray-400">for the period</p>
                           </div>
                           <div className="px-4 py-4 text-center">
                              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-400">
                                 Total Revenue
                              </p>
                              <p className="font-serif text-xl font-bold text-gray-900">${fmt(result.revenue)}</p>
                              <p className="mt-1 text-xs text-gray-400">for the period</p>
                           </div>
                           <div className="px-4 py-4 text-center">
                              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-400">
                                 Health Status
                              </p>
                              <span
                                 className="mt-1 inline-block rounded-full px-3 py-1 text-sm font-bold"
                                 style={{
                                    color: result.statusColor,
                                    backgroundColor: result.statusColor + "18",
                                    border: `1px solid ${result.statusColor}30`,
                                 }}
                              >
                                 {result.statusLabel}
                              </span>
                              <p className="mt-1 text-xs text-gray-400">{result.statusNote}</p>
                           </div>
                        </div>
                     </div>

                     {/* Breakdown — not shown for percentage-only tab */}
                     {tab !== "percentage" && (
                        <div className="overflow-hidden rounded-lg border border-gray-100 text-sm">
                           {(
                              [
                                 ["Wages & Salaries", result.wages],
                                 ["Overtime Pay", result.overtime],
                                 ["Benefits", result.benefits],
                                 ["Payroll Taxes", result.taxes],
                                 ...(result.other > 0 ? [["Other Labor Costs", result.other] as [string, number]] : []),
                              ] as [string, number][]
                           ).map(([label, val]) => (
                              <div key={label} className="flex justify-between border-b border-gray-100 px-4 py-3">
                                 <span className="text-gray-600">{label}</span>
                                 <span className="font-semibold text-gray-800">${fmt(val)}</span>
                              </div>
                           ))}
                           <div className="flex justify-between bg-gray-900 px-4 py-3 font-bold text-white">
                              <span>Total Labor Cost</span>
                              <span>${fmt(result.total)}</span>
                           </div>
                        </div>
                     )}
                  </div>
               ) : (
                  !error && (
                     <div className="mt-4 flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-gray-100 bg-gray-50 py-10 text-center text-gray-400">
                        <svg
                           className="h-10 w-10 opacity-30"
                           viewBox="0 0 24 24"
                           fill="none"
                           stroke="currentColor"
                           strokeWidth={1.5}
                        >
                           <path
                              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                           />
                        </svg>
                        <p className="text-custom-base-bold text-gray-500">Ready to calculate</p>
                        <p className="text-custom-sm-normal">Enter your figures above to see your labor cost breakdown.</p>
                     </div>
                  )
               )}
            </div>
         </div>

         {/* Action buttons */}
         <div className="mb-10 grid grid-cols-3 gap-3">
            {[
               { label: resultCopied ? "Copied!" : "Copy Result", icon: <Copy className="h-4 w-4" />, onClick: copyResult },
               { label: shareCopied ? "Link Copied!" : "Share Link", icon: <Share2 className="h-4 w-4" />, onClick: shareLink },
               { label: "Print", icon: <Printer className="h-4 w-4" />, onClick: () => window.print() },
            ].map(({ label, icon, onClick }) => (
               <button
                  key={label}
                  onClick={onClick}
                  className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-3 text-sm font-semibold text-gray-600 transition hover:border-[#EB5A23] hover:bg-orange-50 hover:text-[#EB5A23]"
               >
                  {icon}
                  <span className="hidden sm:inline">{label}</span>
               </button>
            ))}
         </div>
      </>
   )
}
